import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayoutApple, CardApple, ButtonApple } from '@/components/apple-ui';
import {
  Settings, User, Shield, CreditCard, Bell, LogOut, Loader2, Check,
  Eye, EyeOff, Key, AlertCircle, Moon, Sun, Mail, RefreshCw, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/apple-design-system.css';
import { useTheme } from '@/contexts/ThemeContext';
import { usePlan } from '@/contexts/PlanContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPageApple = ({ user: userProp, planInfo: planInfoProp, onLogout }) => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user: userCtx, planInfo: planInfoCtx, refreshPlanInfo } = usePlan();
  const user = userCtx || userProp;
  const planInfo = planInfoCtx || planInfoProp;
  const avatarKey = `user_avatar_${user?.id || user?.email || 'anon'}`;

  // Fetch user data if missing (e.g. after impersonation)
  React.useEffect(() => {
    if (!user) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data) {
              localStorage.setItem('user', JSON.stringify(data));
              refreshPlanInfo();
            }
          }).catch(() => {});
      }
    }
  }, []);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(() => localStorage.getItem(avatarKey) || localStorage.getItem('user_avatar'));
  const [avatarUploading, setAvatarUploading] = useState(false);
  
  // Password change form - OTP-based flow
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [changePasswordStep, setChangePasswordStep] = useState('idle'); // idle | otp_sent | otp_input | done
  const [changeOtp, setChangeOtp] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  const handleSendChangeOTP = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password/send-otp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('OTP sent to your email');
        setChangePasswordStep('otp_input');
        setOtpCooldown(60);
        const timer = setInterval(() => {
          setOtpCooldown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
        }, 1000);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!changeOtp || changeOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          otp_code: changeOtp,
          new_password: passwordForm.newPassword
        })
      });

      if (res.ok) {
        toast.success('Password changed successfully');
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setChangeOtp('');
        setChangePasswordStep('idle');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only JPG and PNG images allowed');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Image must be under 500KB');
      return;
    }
    setAvatarUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        // Resize to 300×300 square via canvas for crisp display at any size
        const SIZE = 300;
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = ev.target.result;
        });
        const canvas = document.createElement('canvas');
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // Center-crop to square
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/user/avatar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ avatar: dataUrl })
        });
        if (!res.ok) {
          const d = await res.json();
          toast.error(d.detail || 'Upload failed');
          return;
        }
        localStorage.setItem(avatarKey, dataUrl);
        localStorage.setItem('user_avatar', dataUrl);
        setAvatar(dataUrl);
        window.dispatchEvent(new CustomEvent('avatar-updated', { detail: dataUrl }));
        toast.success('Profile photo updated');
      } catch {
        toast.error('Upload failed');
      } finally {
        setAvatarUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = async () => {
    setAvatarUploading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/user/avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avatar: null })
      });
      localStorage.removeItem(avatarKey);
      localStorage.removeItem('user_avatar');
      setAvatar(null);
      window.dispatchEvent(new CustomEvent('avatar-updated', { detail: null }));
      toast.success('Profile photo removed');
    } catch {
      toast.error('Failed to remove photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const getInitials = (user) => {
    const emailParts = (user?.email || '').split('@')[0].split(/[._-]/);
    if (user?.name) {
      const nameParts = user.name.trim().split(/\s+/);
      if (nameParts.length >= 2) return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      if (emailParts.length >= 2 && emailParts[1]) return (nameParts[0][0] + emailParts[1][0]).toUpperCase();
      return nameParts[0].slice(0, 2).toUpperCase();
    }
    if (emailParts.length >= 2 && emailParts[1]) return (emailParts[0][0] + emailParts[1][0]).toUpperCase();
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return 'U';
  };

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      onLogout();
      navigate('/login');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: isDarkMode ? Moon : Sun },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-settings-page">
        {/* Header */}
        <div className="apple-content-header">
          <h1 className="apple-content-title flex items-center gap-3">
            <Settings className="text-gray-600" size={28} />
            Settings
          </h1>
          <p className="apple-content-subtitle">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <CardApple className="p-2">
              <CardApple.Body className="p-0">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all ${
                      activeTab === tab.id 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`settings-tab-${tab.id}`}
                  >
                    <tab.icon size={18} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
                <hr className="my-2 border-gray-100" />
                <button
                  onClick={handleLogoutClick}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="settings-logout-btn"
                >
                  <LogOut size={18} />
                  <span className="font-medium">Sign Out</span>
                </button>
              </CardApple.Body>
            </CardApple>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <CardApple>
                <CardApple.Header>
                  <CardApple.Title>Profile Information</CardApple.Title>
                </CardApple.Header>
                <CardApple.Body className="space-y-6">
                  {/* Avatar Upload */}
                  <div className="flex items-center gap-5">
                    <div className="relative flex-shrink-0">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
                        {avatar
                          ? <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                          : getInitials(user)
                        }
                      </div>
                      <label
                        htmlFor="avatar-upload"
                        className="absolute bottom-0 right-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors"
                        title="Upload photo"
                      >
                        {avatarUploading ? <Loader2 size={12} className="text-white animate-spin" /> : <Camera size={12} className="text-white" />}
                      </label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        className="hidden"
                        onChange={handleAvatarChange}
                        disabled={avatarUploading}
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{user?.name || 'Profile Photo'}</p>
                      <p className="text-sm text-gray-500 mt-0.5">JPG or PNG, max 500KB</p>
                      {avatar && (
                        <button
                          onClick={handleRemoveAvatar}
                          disabled={avatarUploading}
                          className="text-xs text-red-500 hover:text-red-700 mt-1 transition-colors"
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="apple-input-group">
                    <label className="apple-label">Email Address</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="apple-input bg-gray-50"
                      data-testid="settings-email-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>
                  
                  <div className="apple-input-group">
                    <label className="apple-label">Account Type</label>
                    <div className="flex items-center gap-3">
                      <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                        planInfo?.plan === 'free' 
                          ? 'bg-gray-100 text-gray-700' 
                          : planInfo?.plan === 'professional'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {planInfo?.plan_name || planInfo?.plan || 'Free'}
                      </span>
                      {planInfo?.plan === 'free' && (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="text-sm text-blue-600 hover:underline font-medium"
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  </div>

                  {planInfo && (
                    <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <p className="text-sm text-gray-600">
                        <strong>Usage this month:</strong> {planInfo.monthly_usage?.toLocaleString() || 0} / {planInfo.monthly_quota?.toLocaleString() || 'Unlimited'} words
                      </p>
                      {planInfo.days_remaining !== undefined && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Billing cycle:</strong> {planInfo.days_remaining} days remaining
                        </p>
                      )}
                    </div>
                  )}
                </CardApple.Body>
              </CardApple>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <CardApple>
                <CardApple.Header>
                  <CardApple.Title>Appearance</CardApple.Title>
                </CardApple.Header>
                <CardApple.Body className="space-y-6">
                  <div>
                    <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--apple-text-primary)' }}>
                      Theme
                    </h3>
                    <p className="text-sm mb-6" style={{ color: 'var(--apple-text-secondary)' }}>
                      Choose your preferred color scheme
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* Light Mode */}
                      <button
                        onClick={() => !isDarkMode || toggleTheme()}
                        className={`p-5 rounded-2xl border-2 transition-all text-left ${
                          !isDarkMode 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-transparent hover:border-gray-200'
                        }`}
                        style={{ 
                          background: !isDarkMode ? 'var(--apple-blue-light)' : 'var(--apple-fill-secondary)',
                          borderColor: !isDarkMode ? 'var(--apple-blue)' : 'transparent'
                        }}
                        data-testid="theme-light-btn"
                      >
                        <div className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                          <Sun size={24} style={{ color: '#FF9500' }} />
                        </div>
                        <h4 className="font-semibold" style={{ color: 'var(--apple-text-primary)' }}>Light</h4>
                        <p className="text-sm mt-1" style={{ color: 'var(--apple-text-tertiary)' }}>
                          Clean, bright interface
                        </p>
                      </button>
                      
                      {/* Dark Mode */}
                      <button
                        onClick={() => isDarkMode || toggleTheme()}
                        className={`p-5 rounded-2xl border-2 transition-all text-left`}
                        style={{ 
                          background: isDarkMode ? 'var(--apple-blue-light)' : 'var(--apple-fill-secondary)',
                          borderColor: isDarkMode ? 'var(--apple-blue)' : 'transparent'
                        }}
                        data-testid="theme-dark-btn"
                      >
                        <div className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center" style={{ background: '#1c1c1e' }}>
                          <Moon size={24} style={{ color: '#BF5AF2' }} />
                        </div>
                        <h4 className="font-semibold" style={{ color: 'var(--apple-text-primary)' }}>Dark</h4>
                        <p className="text-sm mt-1" style={{ color: 'var(--apple-text-tertiary)' }}>
                          Easy on the eyes
                        </p>
                      </button>
                      
                      {/* System Default */}
                      <button
                        onClick={() => {
                          localStorage.removeItem('apple-ui-theme');
                          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                          if (prefersDark !== isDarkMode) toggleTheme();
                        }}
                        className={`p-5 rounded-2xl border-2 transition-all text-left`}
                        style={{ 
                          background: 'var(--apple-fill-secondary)',
                          borderColor: 'transparent'
                        }}
                        data-testid="theme-system-btn"
                      >
                        <div className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFFFFF 50%, #1c1c1e 50%)' }}>
                          <Settings size={24} style={{ color: '#007AFF' }} />
                        </div>
                        <h4 className="font-semibold" style={{ color: 'var(--apple-text-primary)' }}>System</h4>
                        <p className="text-sm mt-1" style={{ color: 'var(--apple-text-tertiary)' }}>
                          Follow system preference
                        </p>
                      </button>
                    </div>
                  </div>

                  <div className="pt-4" style={{ borderTop: '1px solid var(--apple-separator)' }}>
                    <div className="flex items-center justify-between p-4 rounded-2xl" style={{ background: 'var(--apple-fill-secondary)' }}>
                      <div>
                        <h4 className="font-semibold" style={{ color: 'var(--apple-text-primary)' }}>
                          Current Theme
                        </h4>
                        <p className="text-sm" style={{ color: 'var(--apple-text-tertiary)' }}>
                          {isDarkMode ? 'Dark mode is active' : 'Light mode is active'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'var(--apple-fill-primary)' }}>
                        {isDarkMode ? <Moon size={18} style={{ color: 'var(--apple-purple)' }} /> : <Sun size={18} style={{ color: 'var(--apple-orange)' }} />}
                        <span className="font-medium" style={{ color: 'var(--apple-text-primary)' }}>
                          {isDarkMode ? 'Dark' : 'Light'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardApple.Body>
              </CardApple>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <CardApple>
                <CardApple.Header>
                  <CardApple.Title>Change Password</CardApple.Title>
                </CardApple.Header>
                <CardApple.Body className="space-y-5">
                  {changePasswordStep === 'idle' && (
                    <div>
                      <p className="text-sm text-gray-500 mb-4">To change your password, we'll send a verification code to your registered email.</p>
                      <ButtonApple
                        onClick={handleSendChangeOTP}
                        disabled={saving}
                        icon={saving ? Loader2 : Mail}
                        data-testid="settings-send-otp-btn"
                      >
                        {saving ? 'Sending...' : 'Send Verification Code'}
                      </ButtonApple>
                    </div>
                  )}

                  {changePasswordStep === 'otp_input' && (
                    <form onSubmit={handlePasswordChange}>
                      <div className="space-y-5">
                        <div className="apple-input-group">
                          <label className="apple-label">Verification Code (sent to your email)</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="Enter 6-digit OTP"
                            value={changeOtp}
                            onChange={(e) => setChangeOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="apple-input"
                            required
                            data-testid="settings-otp-input"
                          />
                          <button
                            type="button"
                            onClick={handleSendChangeOTP}
                            disabled={otpCooldown > 0 || saving}
                            className="text-sm text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
                            data-testid="settings-resend-otp-btn"
                          >
                            <RefreshCw size={12} />
                            {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend OTP'}
                          </button>
                        </div>

                        <div className="apple-input-group">
                          <label className="apple-label">New Password</label>
                          <div className="relative">
                            <input
                              type={showPasswords.new ? 'text' : 'password'}
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                              className="apple-input pr-12"
                              required
                              minLength={8}
                              data-testid="settings-new-password"
                            />
                            <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                              {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <div className="apple-input-group">
                          <label className="apple-label">Confirm New Password</label>
                          <div className="relative">
                            <input
                              type={showPasswords.confirm ? 'text' : 'password'}
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                              className="apple-input pr-12"
                              required
                              data-testid="settings-confirm-password"
                            />
                            <button type="button" onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                              {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>

                        <ButtonApple
                          type="submit"
                          disabled={saving}
                          icon={saving ? Loader2 : Key}
                          data-testid="settings-change-password-btn"
                        >
                          {saving ? 'Changing...' : 'Change Password'}
                        </ButtonApple>
                      </div>
                    </form>
                  )}
                </CardApple.Body>
              </CardApple>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <CardApple>
                <CardApple.Header>
                  <CardApple.Title>Billing & Subscription</CardApple.Title>
                </CardApple.Header>
                <CardApple.Body className="space-y-6">
                  <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Current Plan</p>
                        <p className="text-3xl font-bold text-gray-900 capitalize">
                          {planInfo?.plan_name || planInfo?.plan || 'Free'}
                        </p>
                      </div>
                      <ButtonApple
                        onClick={() => navigate('/pricing')}
                        data-testid="settings-view-plans-btn"
                      >
                        View Plans
                      </ButtonApple>
                    </div>
                    
                    {planInfo && (
                      <div className="grid grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-sm text-gray-500">Monthly Quota</p>
                          <p className="text-lg font-semibold text-gray-900">{planInfo.monthly_quota?.toLocaleString() || 'Unlimited'} words</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Used This Month</p>
                          <p className="text-lg font-semibold text-gray-900">{planInfo.monthly_usage?.toLocaleString() || 0} words</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Wallet Balance for PAYG */}
                  {planInfo?.plan === 'payg' && (
                    <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Wallet Balance</p>
                          <p className="text-2xl font-bold text-blue-900">₹{planInfo.wallet_balance?.toLocaleString() || 0}</p>
                        </div>
                        <ButtonApple size="sm">Top Up</ButtonApple>
                      </div>
                    </div>
                  )}

                  <ButtonApple
                    variant="secondary"
                    onClick={() => navigate('/billing')}
                    icon={CreditCard}
                    data-testid="settings-view-history-btn"
                  >
                    View Billing
                  </ButtonApple>
                </CardApple.Body>
              </CardApple>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <CardApple>
                <CardApple.Header>
                  <CardApple.Title>Notification Preferences</CardApple.Title>
                </CardApple.Header>
                <CardApple.Body className="space-y-2">
                  {[
                    { id: 'email_updates', label: 'Email Updates', desc: 'Receive product updates and tips via email', defaultChecked: true },
                    { id: 'translation_complete', label: 'Translation Complete', desc: 'Get notified when batch translations finish', defaultChecked: true },
                    { id: 'usage_alerts', label: 'Usage Alerts', desc: 'Alert when approaching monthly quota limit', defaultChecked: true },
                    { id: 'weekly_summary', label: 'Weekly Summary', desc: 'Receive weekly activity summary', defaultChecked: false },
                    { id: 'marketing', label: 'Marketing Emails', desc: 'Occasional promotional offers and updates', defaultChecked: false },
                  ].map(item => (
                    <label 
                      key={item.id} 
                      className="flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input 
                        type="checkbox" 
                        defaultChecked={item.defaultChecked}
                        className="mt-1 w-5 h-5 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500" 
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </label>
                  ))}
                  
                  <div className="pt-4 border-t border-gray-100">
                    <ButtonApple icon={Check}>Save Preferences</ButtonApple>
                  </div>
                </CardApple.Body>
              </CardApple>
            )}
          </div>
        </div>
      </div>
    </MainLayoutApple>
  );
};

export default SettingsPageApple;
